import {
  ApiBlueprint,
  createApiFactory,
  createFrontendPlugin,
  discoveryApiRef,
  fetchApiRef,
  PageBlueprint,
  SubPageBlueprint,
} from '@backstage/frontend-plugin-api';
import { Content } from '@backstage/core-components';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import type { Entity } from '@backstage/catalog-model';
import {
  annotationList,
  CI_WORKFLOWS_ANNOTATION,
  LAMBDA_FUNCTIONS_ANNOTATION,
  SOURCE_PATHS_ANNOTATION,
} from '@internal/backstage-plugin-delivery-common';
import { deliveryApiRef, DeliveryClient } from './api';

const has = (annotation: string) => (entity: Entity) =>
  annotationList(entity, annotation).length > 0;

const deliveryApi = ApiBlueprint.make({
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: deliveryApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) =>
          new DeliveryClient(discoveryApi, fetchApi),
      }),
    ),
});

export const pullRequestsCard = EntityCardBlueprint.make({
  name: 'pull-requests',
  params: {
    filter: has(SOURCE_PATHS_ANNOTATION),
    loader: () =>
      import('./components/PullRequestsCard').then(m => <m.PullRequestsCard />),
  },
});

export const ciRunsCard = EntityCardBlueprint.make({
  name: 'ci-runs',
  params: {
    filter: has(CI_WORKFLOWS_ANNOTATION),
    loader: () => import('./components/CiRunsCard').then(m => <m.CiRunsCard />),
  },
});

export const opsLinksCard = EntityCardBlueprint.make({
  name: 'ops-links',
  params: {
    // Compact card: shown in the properties column of the entity overview.
    type: 'info',
    filter: has(LAMBDA_FUNCTIONS_ANNOTATION),
    loader: () =>
      import('./components/OpsLinksCard').then(m => <m.OpsLinksCard />),
  },
});

export const releasesCard = EntityCardBlueprint.make({
  name: 'releases',
  params: {
    // The monorepo's System (app-bobbee's catalog-info.yaml), the only System with a repository.
    filter: (entity: Entity) =>
      entity.kind.toLowerCase() === 'system' &&
      Boolean(entity.metadata.annotations?.['github.com/project-slug']),
    loader: () =>
      import('./components/ReleasesCard').then(m => <m.ReleasesCard />),
  },
});

export const ticketSearchCard = EntityCardBlueprint.make({
  name: 'ticket-search',
  params: {
    // Same System as the releases card: find a ticket without leaving its page.
    filter: (entity: Entity) =>
      entity.kind.toLowerCase() === 'system' &&
      Boolean(entity.metadata.annotations?.['github.com/project-slug']),
    loader: () =>
      import('./components/ReleasesPage').then(m => <m.TicketSearch />),
  },
});

const deliveryPage = PageBlueprint.make({
  params: {
    path: '/delivery',
    title: 'Livraison',
    loader: () =>
      import('./components/DeliveryPage').then(m => <m.DeliveryPage />),
  },
});

/** Settings tab « Données »: the data sources, their progress and a reload now. */
const dataSourcesSettings = SubPageBlueprint.make({
  name: 'data-sources',
  // A tab of the user settings page, which belongs to another plugin.
  attachTo: { id: 'page:user-settings', input: 'pages' },
  params: {
    path: 'data',
    title: 'Données',
    loader: () =>
      import('./components/DataSourcesSettings').then(m => (
        <Content>
          <m.DataSourcesSettings />
        </Content>
      )),
  },
});

/** Delivery cards for app-bobbee components (docs/catalog-annotations.md) and the release radar. */
export default createFrontendPlugin({
  pluginId: 'delivery',
  // Header icon of pages without their own: same as the sidebar entry
  icon: <LocalShippingIcon fontSize="inherit" />,
  extensions: [
    deliveryApi,
    pullRequestsCard,
    ciRunsCard,
    opsLinksCard,
    releasesCard,
    ticketSearchCard,
    deliveryPage,
    dataSourcesSettings,
  ],
});
